import { encode } from "gpt-token-utils";
import {
  ChatCompletionRequestMessage,
  Configuration,
  OpenAIApi,
  CreateChatCompletionResponse,
} from "openai";
import { OpenAIExt } from "openai-ext";
import { ProviderSettings, Settings } from "../db";
import { Provider, ProviderModel, providerRegistry } from "../utils/providers";

// Define initial OpenAI models
const openAIModels: ProviderModel[] = [
  {
    id: "gpt-3.5-turbo",
    name: "GPT-3.5 Turbo",
    description: "Most capable GPT-3.5 model, optimized for chat",
    contextWindow: 4096,
    maxTokens: 4096,
  },
  {
    id: "gpt-4",
    name: "GPT-4",
    description: "Most capable GPT-4 model, optimized for chat",
    contextWindow: 8192,
    maxTokens: 8192,
  },
  {
    id: "gpt-4o",
    name: "GPT-4 Omni",
    description: "Latest GPT-4 model with improved capabilities",
    contextWindow: 128000,
    maxTokens: 4096,
  },
];

// Helper function to get provider settings from the main settings object
function getProviderSettings(settings: Settings): ProviderSettings {
  const providerSettings = settings.providerSettings?.[settings.provider || 'openai'];
  if (!providerSettings) {
    throw new Error("Provider settings not found");
  }
  return providerSettings;
}

// Helper function to get OpenAI client using provider settings
function getOpenAIClient(settings: Settings): OpenAIApi {
  // Get provider-specific settings
  const providerSettings = getProviderSettings(settings);
  
  const apiKey = providerSettings.apiKey || '';
  const apiType = providerSettings.apiType || 'openai';
  const apiAuth = providerSettings.apiAuth || 'bearer-token';
  const basePath = providerSettings.apiBase;

  const configuration = new Configuration({
    ...((apiType === "openai" ||
      (apiType === "custom" && apiAuth === "bearer-token")) && {
      apiKey: apiKey,
    }),
    ...(apiType === "custom" && basePath && { basePath }),
  });
  return new OpenAIApi(configuration);
}

// Helper function to set stream content
function setStreamContent(
  messageId: string,
  content: string,
  isFinal: boolean,
  db: any
) {
  content = isFinal ? content : content + "█";
  db.messages.update(messageId, { content: content });
}

// Helper function to set total tokens
function setTotalTokens(chatId: string, content: string, db: any) {
  let total_tokens = encode(content).length;
  db.chats.where({ id: chatId }).modify((chat: any) => {
    if (chat.totalTokens) {
      chat.totalTokens += total_tokens;
    } else {
      chat.totalTokens = total_tokens;
    }
  });
}

// Create the OpenAI provider
const openAIProvider: Provider = {
  id: "openai",
  name: "OpenAI",
  description: "Official OpenAI API provider",
  authType: "bearer-token",
  apiKeyUrl: "https://platform.openai.com/account/api-keys",
  defaultModel: "gpt-3.5-turbo",
  models: openAIModels,

  // Fetch models from OpenAI API
  async fetchModels(settings: Settings): Promise<ProviderModel[]> {
    try {
      const client = getOpenAIClient(settings);
      
      // Use the SDK to list models
      const response = await client.listModels();
      
      // Process the models data
      if (response.data && response.data.data) {
        // Filter for GPT models only, and create ProviderModel objects
        const models = response.data.data
          .filter((model: any) => {
            const id = model.id.toLowerCase();
            return id.includes('gpt') && id.includes('ft') === false;
          })
          .map((model: any) => {
            return {
              id: model.id,
              name: model.id.replace('gpt-', 'GPT-').toUpperCase(),
              description: `${model.id} - ${model.object}`,
              // Add standard context window and token limits based on model ID
              contextWindow: model.id.includes('32k') ? 32768 :
                            model.id.includes('16k') ? 16384 :
                            model.id.includes('gpt-4-turbo') ? 128000 :
                            model.id.includes('gpt-4o') ? 128000 :
                            model.id.includes('gpt-4') ? 8192 : 4096,
              maxTokens: 4096 // Standard max response tokens
            };
          })
          .sort((a: any, b: any) => a.name.localeCompare(b.name));
        
        console.log(`Fetched ${models.length} OpenAI models via SDK`);
        return models as ProviderModel[];
      } else {
        throw new Error("Invalid response format from OpenAI API");
      }
    } catch (error: any) {
      console.error("Error fetching OpenAI models:", error);
      // If the API call fails, log the error and return the default models
      return openAIModels;
    }
  },

  // Validate credentials
  async validateCredentials(settings: Settings): Promise<boolean> {
    try {
      const providerSettings = getProviderSettings(settings);
      
      if (!providerSettings.apiKey) {
        throw new Error("API Key is required");
      }

      const client = getOpenAIClient(settings);

      await client.createChatCompletion({
        model: "gpt-3.5-turbo",
        messages: [{ role: "user", content: "Hello" }],
        max_tokens: 1,
      });

      return true;
    } catch (error: any) {
      console.error("OpenAI validation error:", error);
      const message = error.response?.data?.error?.message || error.message;
      throw new Error(`OpenAI API Key validation failed: ${message}`);
    }
  },

  // Create chat completion
  async createChatCompletion(
    settings: Settings,
    messages: ChatCompletionRequestMessage[]
  ): Promise<CreateChatCompletionResponse> {
    try {
      const providerSettings = getProviderSettings(settings);
      
      if (!providerSettings.apiKey) {
        throw new Error("API Key is required");
      }

      if (!settings.model) {
        throw new Error("Model is not configured");
      }

      const client = getOpenAIClient(settings);

      const response = await client.createChatCompletion({
        model: settings.model,
        messages,
        stream: false,
      });

      return response.data;
    } catch (error: any) {
      console.error("OpenAI chat completion error:", error);
      const message = error.response?.data?.error?.message || error.message;
      throw new Error(`OpenAI chat completion failed: ${message}`);
    }
  },

  // Create streaming chat completion
  async createStreamChatCompletion(
    settings: Settings,
    messages: ChatCompletionRequestMessage[],
    chatId: string,
    messageId: string
  ): Promise<any> {
    try {
      const providerSettings = getProviderSettings(settings);
      
      if (!providerSettings.apiKey) {
        throw new Error("API Key is required");
      }

      if (!settings.model) {
        throw new Error("Model is not configured");
      }

      // We need to import db here to avoid circular dependency
      const { db } = await import("../db");

      return OpenAIExt.streamClientChatCompletion(
        {
          model: settings.model,
          messages,
        },
        {
          apiKey: providerSettings.apiKey,
          handler: {
            onContent(content, isFinal, stream) {
              setStreamContent(messageId, content, isFinal, db);
              if (isFinal) {
                setTotalTokens(chatId, content, db);
              }
            },
            onDone(stream) {},
            onError(error, stream) {
              console.error("OpenAI streaming error:", error);
              db.messages.update(messageId, {
                content: `Error: ${error.message}`,
              });
            },
          },
        }
      );
    } catch (error: any) {
      console.error("OpenAI streaming error:", error);
      const message = error.response?.data?.error?.message || error.message;
      throw new Error(`OpenAI streaming failed: ${message}`);
    }
  },
};

// Register the OpenAI provider
providerRegistry.register(openAIProvider);

export default openAIProvider;
